//Imports
import React, {
    useEffect,
    useRef,
    useState,
} from "react";

//Hooks
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { splitTextIntoLines } from 'Core/data/Helpers';
import { HasPermission } from "Core/security"

//Controls
import {
    IconButton,
    MenuComponent,
    SearchInput,
    TableButtons,
    TableView,
    useTablePagination
} from "@gpstrackit/gtc-react-controls";

//Components
import { NotificationManager } from "../../../../components/Notifications";
import { Grid } from "@mui/material";

//Actions
import { sendGenericExport, openModalCreateEditWorkOrderStatus, openModalDeleteWorkOrderStatus, clearRedux } from "Redux/actions";
import { manageSearchWorkOrderStatus, reset } from './redux/slice';

//Views
import CreateEditManageWorkOrderStatus from './modals/createEditManageWorkOrderStatus/index';
import DeleteManageWorkOrderStatus from './modals/deleteManageWorkOrderStatus/index';

const WorkOrderStatus = (props) => {

    const dispatch = useDispatch();

    //Table refs
    const workOrderStatusTableRef = useRef();

    //Language
    const { messages, locale } = useIntl();


    const paginationWorkOrderStatus = useTablePagination({
        dispatch,
        action: manageSearchWorkOrderStatus,
        searchFields: ["name", "description"],
        tableRef: workOrderStatusTableRef,
        sendGenericExport,
        exportName: "work-order-status",
    });

    // Permissions
    const user = useSelector((state) => state.securityRedux.user);
    const editablePermissions = HasPermission(user, "", [350])

    //Local States
    const [selectedItems, setSelectedItems] = useState([]);

    //Redux States
    const openModalCreateEdit = useSelector((state) => state.manageCreateEditWorkOrderStatusRedux.open);
    const openModalDelete = useSelector((state) => state.manageDeleteManageWorkOrderStatusRedux.open);
    const { loading, items, total } = useSelector((state) => state.manageWorkOrderStatusTabRedux);
    const exportResult = useSelector((state) => state.commomRedux.exportResult);
    const exportName = useSelector((state) => state.commomRedux.exportName);

    useEffect(() => {
        paginationWorkOrderStatus.setColumns([
            {
                key: "name",
                title: messages['manage_workOrderStatus_name'],
                functionString: 'return row?.name',
                show: true,
                export: true,
                sort: true,
                width: 200,
                exportValue: (row) => row?.name,
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item xs={10}>
                            <p>{row.name}</p>
                        </Grid>
                        <Grid item xs={2}>
                            {
                                editablePermissions && row.companyId !== -1 &&
                                <MenuComponent
                                    placement="right-start"
                                    options={[messages['edit'], messages['delete']]}
                                    icon="more_vert"
                                    className="showInHover"
                                    onClick={(value) => {
                                        if (value === messages['edit']) {
                                            dispatch(openModalCreateEditWorkOrderStatus(row?.id));
                                        } else if (value === messages['delete']) {
                                            dispatch(openModalDeleteWorkOrderStatus({ workOrderStatus: row }))
                                        }
                                    }}
                                    value={row}
                                />
                            }
                        </Grid>
                    </Grid>
                ),
            },
            {
                key: "color",
                title: messages['manage_workOrderStatus_color'],
                functionString: 'return row?.color',
                show: true,
                export: true,
                width: 100,
                exportValue: (row) => row?.color,
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item>
                            <div
                                style={{
                                    backgroundColor: `#${row?.color?.replace('#', '')}`,
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '4px',
                                    marginRight: '10px'
                                }}
                            ></div>
                        </Grid>
                    </Grid>
                ),
            },
            {
                key: "isDefault",
                title: messages['manage_workOrderStatus_default'],
                functionString: 'return row?.isDefault ? "Yes" : "No"',
                show: true,
                export: true,
                sort: true,
                width: 140,
                exportValue: (row) => row?.isDefault ? messages['yes'] : messages['no'],
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item style={{ wordWrap: 'break-word' }}>
                            {row?.isDefault ? messages['yes'] : messages['no']}
                        </Grid>
                    </Grid>
                ),
            },
            {
                key: "description",
                title: messages['manage_workOrderStatus_description'],
                functionString: `
                    function splitTextIntoLines(text, maxLength) {
                        let result = "";
                        for (let i = 0; i < text.length; i += maxLength) {
                            result += text.slice(i, i + maxLength) + "\\n";
                        }
                        return result.trim();
                    };
                    return row?.description ? splitTextIntoLines(row?.description, 50) : "";
                `,
                show: true,
                export: true,
                sort: true,
                exportValue: (row) => row?.description ? splitTextIntoLines(row?.description, 50) : "",
                render: (row) => (
                    <Grid container alignItems="center">
                        <Grid item style={{ wordWrap: 'break-word', width: 350 }}>
                            {row?.description}
                        </Grid>
                    </Grid>
                ),
            }
        ]);
        paginationWorkOrderStatus.onChangeSort({ key: "name", order: "asc" });
        return () => dispatch(reset());
    }, [locale]);

    useEffect(() => {
        if (!exportResult || exportName !== "work-order-status") return;

        NotificationManager.info(
            messages["exportInProcessBody"],
            messages["exportInProcessTitle"],
            4000,
            null,
            null,
            "info",
            null
        );
        dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }, [exportResult, exportName]);

    const refreshTable = () => {
        setSelectedItems([]);
        workOrderStatusTableRef?.current?.setSelectedItems([]);
        workOrderStatusTableRef?.current?.setPage(1);
        paginationWorkOrderStatus.onChangePage(1);
    };

    return (
        <div maxWidth={false}>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: "flex" }}>
                            {editablePermissions &&
                                <>
                                    <IconButton
                                        tooltip={messages["add"]}
                                        placement="bottom"
                                        variant="secondary"
                                        icon="add_box"
                                        size="small"
                                        disabled={loading}
                                        onClick={() => {
                                            dispatch(openModalCreateEditWorkOrderStatus());
                                        }}
                                    />
                                    {
                                        selectedItems?.length && selectedItems.some((item) => item?.companyId !== -1) ?
                                            <IconButton
                                                tooltip={messages["delete"]}
                                                placement="bottom"
                                                variant="secondary"
                                                icon="delete"
                                                size="small"
                                                onClick={() => {
                                                    let items = selectedItems.filter((item) => item?.companyId !== -1)
                                                    dispatch(openModalDeleteWorkOrderStatus({ workOrderStatusList: items }))
                                                }}
                                                style={{ opacity: !selectedItems.length && "0.6" }}
                                                isDisabled={!selectedItems.length}
                                            /> : <></>
                                    }
                                </>
                            }
                            <TableButtons
                                messages={messages}
                                {...paginationWorkOrderStatus}
                                tableRef={workOrderStatusTableRef}
                                buttons={["export", "orderColumn"]}
                                onExport={(type) => {
                                    paginationWorkOrderStatus.onExport(type);
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages["manage_workOrderStatus_search_by_name_description"]}
                        searchText={paginationWorkOrderStatus.searchText}
                        onSearchText={paginationWorkOrderStatus.onSearchChange}
                    />
                </Grid>
            </Grid>
            <TableView
                keyColumn="id"
                loading={loading}
                topheight={235}
                data={items}
                total={total}
                exportOrientation="landscape"
                onSelectedItems={(items) => setSelectedItems(items)}
                {...paginationWorkOrderStatus}
                hideDragable
                hideAllInPagination
            />
            {openModalCreateEdit && <CreateEditManageWorkOrderStatus refresh={refreshTable} />}
            {openModalDelete && <DeleteManageWorkOrderStatus refresh={refreshTable} />}
        </div>
    )

}

export default WorkOrderStatus;